import { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface ChecklistRow {
  id: string;
  title: string;
  summary: string;
  done: boolean;
}

interface GettingStartedModalProps {
  open: boolean;
  rows: ChecklistRow[];
  /** Shown under the list — e.g. that a site's own pages appear once a site is picked. */
  note?: string | null;
  onOpen: (id: string) => void;
  onStartAll: () => void;
  onHide: () => void;
}

/**
 * The "Getting started" checklist: every page the user's menu shows, each with its own tour.
 * Offered on every app open (see TourProvider) and reopened from the "?" menu, the sidebar's
 * Guided tour button, or "All tours" inside a running tour. Styled like ConfirmDialog.
 */
export default function GettingStartedModal({
  open, rows, note, onOpen, onStartAll, onHide,
}: GettingStartedModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onHide(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onHide]);

  if (!open) return null;

  const remaining = rows.filter((r) => !r.done).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overlay-backdrop p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="getting-started-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onHide(); }}
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-modal">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h3 id="getting-started-title" className="text-[16px] font-semibold text-foreground">
            Getting started
          </h3>
          <button
            onClick={onHide}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            A short walk through the pages your role can use. Guided tour in the side menu brings it
            back at any time.
          </p>

          <ul className="mt-4 divide-y divide-border overflow-hidden rounded-lg border border-border">
            {rows.map((r) => (
              <li key={r.id} className="flex items-start gap-3 px-4 py-3">
                {/* A plain bullet, not a checkbox — always brand, whatever the row's state. */}
                <span
                  aria-hidden
                  className="mt-[7px] h-2 w-2 flex-shrink-0 rounded-full bg-brand"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium text-foreground">
                    {r.title}
                    {/* Rows look the same either way; the footer count shows progress, and screen readers hear it. */}
                    {r.done && <span className="sr-only"> (done)</span>}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">{r.summary}</p>
                </div>
                <button
                  onClick={() => onOpen(r.id)}
                  className="flex-shrink-0 rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
                >
                  Open
                </button>
              </li>
            ))}
          </ul>

          {note && <p className="mt-3 text-[12px] text-muted-foreground">{note}</p>}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
          <span className="text-[12px] text-muted-foreground">
            {remaining === 0 ? 'All done' : `${rows.length - remaining} of ${rows.length} done`}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onHide}
              className="rounded-lg px-4 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted"
            >
              Hide for now
            </button>
            <button
              onClick={onStartAll}
              disabled={remaining === 0}
              autoFocus
              className="rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
            >
              Start the tour
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
